import React, { useRef, useState, useMemo, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import {
  ChevronLeft, ChevronRight, List, Download, Loader2,
  Scale, TrendingUp, Landmark, Users, Truck, Handshake,
  Briefcase, UserCircle, FileSignature, AlertTriangle, BookOpen, X,
} from 'lucide-react';
import { getManualChapters, getTotalManualPages, getChapterStartIndexLang } from '../../data/manualContent';
import { downloadFullManualPdf } from '../../utils/fullManualPdf';
import { useTranslation } from '../../i18n';
import ManualPage from './ManualPage';
import ManualSearch from './ManualSearch';

/**
 * Map chapter-icon identifiers (stored as strings in manualContent.js)
 * to the actual Lucide component. Keeps the data file framework-agnostic.
 */
const CHAPTER_ICONS = {
  Scale, TrendingUp, Landmark, Users, Truck, Handshake,
  Briefcase, UserCircle, FileSignature, AlertTriangle, BookOpen,
};

/**
 * Animated 3D flip book — uses react-pageflip for the realistic page-turn.
 * Sidebar shows the chapter index; click jumps to the chapter's first page.
 *
 * Larger format (520×720) so the body text stays comfortably readable
 * (base 14px / 15px on most rows, instead of 11-12px on the previous size).
 *
 * 31/05/2026 — now locale-aware (FR / EN) via getManualChapters.
 */
export default function ManualBook({ initialChapter = null }) {
  const { language } = useTranslation();
  const chapters = useMemo(() => getManualChapters(language), [language]);
  const totalManualPages = getTotalManualPages(language);
  const bookRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showToc, setShowToc] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const fileName = language === 'en' ? 'hst-complete-manual' : 'manuel-hst-complet';
      await downloadFullManualPdf(chapters, fileName);
    } catch (err) {
      console.warn('[manual] full PDF export failed:', err);
    } finally {
      setDownloadingAll(false);
    }
  };

  // Cover + chapter pages + back cover. The book needs an even number of leaves
  // (front+back), so we keep cover at index 0 + back cover at end.
  const pageNodes = useMemo(() => {
    const nodes = [];
    // 1. Front cover
    nodes.push(
      <FrontCoverRef key="front-cover" language={language} />,
    );
    // 2. Chapter pages
    chapters.forEach(chapter => {
      chapter.pages.forEach((page, idx) => {
        nodes.push(
          <ManualPage
            key={`${chapter.id}-${idx}`}
            chapter={chapter}
            page={page}
            chapterPageIndex={idx}
          />,
        );
      });
    });
    // 3. Back cover
    nodes.push(<BackCoverRef key="back-cover" language={language} />);
    return nodes;
  }, [chapters, language]);

  // Open on the contextual chapter when the book mounts.
  useEffect(() => {
    if (!initialChapter) return;
    // Let react-pageflip mount fully before issuing the flip command.
    const timer = setTimeout(() => {
      const startIdx = getChapterStartIndexLang(initialChapter, language) + 1; // +1 past the cover
      bookRef.current?.pageFlip()?.flip(startIdx);
    }, 250);
    return () => clearTimeout(timer);
  }, [initialChapter, language]);

  const handlePrev = () => bookRef.current?.pageFlip()?.flipPrev();
  const handleNext = () => bookRef.current?.pageFlip()?.flipNext();
  const goToChapter = (chapterId) => {
    const startIdx = getChapterStartIndexLang(chapterId, language) + 1; // +1 = past the cover
    bookRef.current?.pageFlip()?.flip(startIdx);
    setShowToc(false);
  };
  const goToAbsolutePage = (absoluteIndex) => {
    // Already includes the +1 offset for the front cover (see ManualSearch.buildIndex).
    bookRef.current?.pageFlip()?.flip(absoluteIndex);
    setShowToc(false);
  };

  return (
    <div className="relative" data-testid="manual-book">
      {/* Toggle ToC */}
      <button
        type="button"
        onClick={() => setShowToc(s => !s)}
        className="absolute -left-14 top-0 w-10 h-10 rounded-full bg-amber-600 hover:bg-amber-500 text-white flex items-center justify-center transition-colors z-10"
        data-testid="manual-toc-toggle"
        aria-label={language === 'en' ? 'Table of contents' : 'Sommaire'}
      >
        <List className="w-4 h-4" />
      </button>

      {/* Download the entire manual as a single PDF */}
      <button
        type="button"
        onClick={handleDownloadAll}
        disabled={downloadingAll}
        className="absolute -left-14 top-12 w-10 h-10 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center transition-colors z-10 disabled:opacity-60"
        data-testid="manual-download-all"
        aria-label={language === 'en' ? 'Download the complete manual' : 'Télécharger tout le manuel en PDF'}
        title={language === 'en' ? 'Download the complete manual (PDF)' : 'Télécharger le manuel complet (PDF)'}
      >
        {downloadingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </button>

      {/* Sommaire side panel — search + chapter list */}
      {showToc && (
        <div
          className="absolute -left-[22rem] top-0 w-80 bg-gradient-to-b from-ink-800 to-ink-900 rounded-xl shadow-2xl border border-ink-600 z-20 overflow-hidden"
          data-testid="manual-toc"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-ink-700/80 bg-ink-900/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-amber-300" />
              <h3 className="text-sm font-bold text-amber-300 tracking-wide">
                {language === 'en' ? 'Contents' : 'Sommaire'}
              </h3>
              <span className="text-[10px] font-mono text-slate-500">
                {chapters.length} {language === 'en' ? 'chapters' : 'chapitres'} · {totalManualPages}p.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowToc(false)}
              className="text-slate-500 hover:text-slate-200 transition-colors"
              data-testid="manual-toc-close"
              aria-label={language === 'en' ? 'Close' : 'Fermer'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-3 border-b border-ink-700/60">
            <ManualSearch
              chapters={chapters}
              language={language}
              onResultClick={goToAbsolutePage}
            />
          </div>

          {/* Chapter list — beautified */}
          <div className="p-2 space-y-1 max-h-[28rem] overflow-y-auto" data-testid="manual-toc-list">
            {chapters.map((c, i) => {
              const Icon = CHAPTER_ICONS[c.icon] || BookOpen;
              return (
                <button
                  key={c.id}
                  onClick={() => goToChapter(c.id)}
                  className="group w-full text-left px-2 py-2 rounded-lg flex items-center gap-3 transition-all border border-transparent hover:bg-ink-700/40 hover:border-ink-600 hover:translate-x-0.5"
                  data-testid={`manual-toc-${c.id}`}
                  style={{ borderLeftColor: c.color, borderLeftWidth: 3, borderLeftStyle: 'solid' }}
                >
                  {/* Icon badge */}
                  <span
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
                    style={{ backgroundColor: `${c.color}33`, color: c.color }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2.25} />
                  </span>
                  {/* Title + subtitle */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                      {language === 'en' ? 'Ch.' : 'Ch.'} {String(i + 1).padStart(2, '0')}
                    </p>
                    <p className="text-sm font-semibold text-slate-100 truncate leading-tight">
                      {c.title}
                    </p>
                    {c.subtitle && (
                      <p className="text-[10px] text-slate-500 truncate mt-0.5">{c.subtitle}</p>
                    )}
                  </div>
                  {/* Page count badge */}
                  <span className="text-[10px] font-mono text-slate-500 group-hover:text-amber-300 transition-colors flex-shrink-0">
                    {c.pages.length}p.
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Page indicator */}
      <div className="absolute -top-9 left-1/2 -translate-x-1/2 text-xs text-slate-400 font-mono" data-testid="manual-page-indicator">
        Page {Math.min(currentPage + 1, totalManualPages + 2)} / {totalManualPages + 2}
      </div>

      {/* The book — 520×720 pour confort de lecture */}
      <HTMLFlipBook
        ref={bookRef}
        width={520}
        height={720}
        size="fixed"
        minWidth={420}
        maxWidth={620}
        minHeight={560}
        maxHeight={820}
        showCover={true}
        flippingTime={650}
        usePortrait={false}
        startPage={0}
        drawShadow={true}
        maxShadowOpacity={0.5}
        mobileScrollSupport={true}
        className="manual-book"
        onFlip={(e) => setCurrentPage(e.data)}
      >
        {pageNodes}
      </HTMLFlipBook>

      {/* Prev / Next */}
      <button
        type="button"
        onClick={handlePrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-14 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="manual-prev"
        aria-label="Page précédente"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={handleNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-14 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="manual-next"
        aria-label="Page suivante"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

function FrontCover({ language }, ref) {
  const en = language === 'en';
  return (
    <div ref={ref} className="manual-cover h-full w-full bg-gradient-to-br from-amber-700 via-amber-800 to-amber-900 text-amber-50 flex flex-col items-center justify-center p-8 text-center select-none">
      <div className="w-20 h-20 rounded-full bg-amber-100/15 border-4 border-amber-100/30 flex items-center justify-center mb-6 shadow-inner">
        <svg viewBox="0 0 24 24" fill="none" className="w-10 h-10 text-amber-100" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 5a2 2 0 0 1 2-2h6v18H5a2 2 0 0 1-2-2V5z" />
          <path d="M21 5a2 2 0 0 0-2-2h-6v18h6a2 2 0 0 0 2-2V5z" />
        </svg>
      </div>
      <h1 className="text-3xl font-display font-black tracking-tight">{en ? 'HST Manual' : 'Manuel HST'}</h1>
      <p className="text-sm mt-2 opacity-80">{en ? 'Pedagogical companion' : 'Accompagnement pédagogique'}</p>
      <div className="mt-8 text-[10px] uppercase tracking-[0.3em] opacity-60">
        Hyper Société de Transport
      </div>
      <div className="mt-12 text-xs opacity-70 italic max-w-xs">
        {en
          ? 'Turn the page to discover 8 chapters: taxation, finance, banking, HR, transport, M&A, CRM and risks.'
          : 'Tournez la page pour découvrir 8 chapitres : fiscalité, finance, banque, RH, transport, M&A, CRM et risques.'}
      </div>
    </div>
  );
}
const FrontCoverRef = React.forwardRef(FrontCover);

function BackCover({ language }, ref) {
  const en = language === 'en';
  return (
    <div ref={ref} className="manual-cover h-full w-full bg-gradient-to-br from-ink-800 via-ink-900 to-black text-amber-50/80 flex flex-col items-center justify-center p-8 text-center select-none">
      <p className="text-xs uppercase tracking-[0.3em] opacity-60">{en ? 'End of manual' : 'Fin du manuel'}</p>
      <div className="mt-4 text-amber-200/90 italic text-sm max-w-xs leading-relaxed">
        {en
          ? '“Taxation, like roads, demands patience and method. Safe travels.”'
          : '« La fiscalité, comme les routes, demande de la patience et de la méthode. Bonne route. »'}
      </div>
      <div className="mt-8 text-[10px] opacity-50">— {en ? 'The HST team' : "L'équipe HST"}</div>
    </div>
  );
}
const BackCoverRef = React.forwardRef(BackCover);
